// @ts-strict-ignore
import {SectionItem} from 'common/components/items-menu/types';
import {Placement} from 'common/components/tooltip/types';

export enum DownloadFormat {
  PNG = `PNG`,
  PDF = `PDF`,
  CSV = `CSV`,
  CSVCLIPBOARD = `CSVCLIPBOARD`,
}

const DOWNLOAD_PREFIX = `download-`;

export function isScreenshotDownloadFormat(format: DownloadFormat): boolean {
  return [DownloadFormat.PNG, DownloadFormat.PDF].includes(format);
}

function isDownloadFormat(format: string): format is DownloadFormat {
  return (Object.values(DownloadFormat) as string[]).includes(format);
}

export function getDownloadFormatFromAction(action: string): DownloadFormat {
  if (!action.startsWith(DOWNLOAD_PREFIX)) {
    return null;
  }
  const format = action.split(`-`)[1];
  return isDownloadFormat(format) ? format : null;
}

type GetDownloadOptionsParams = {
  downloadFormats?: DownloadFormat[];
  hasCanExportCsvFeatureGate?: boolean;
  hasDownloadCsvDataPermission?: boolean;
  exportCsvDefaultTooltipConfig?: {text: string};
};

export function getDownloadOptions({
  downloadFormats = [DownloadFormat.PNG, DownloadFormat.PDF, DownloadFormat.CSV, DownloadFormat.CSVCLIPBOARD],
  hasCanExportCsvFeatureGate,
  hasDownloadCsvDataPermission,
  exportCsvDefaultTooltipConfig,
}: GetDownloadOptionsParams = {}): SectionItem[] {
  const hasPermissionMap = {
    [DownloadFormat.PNG]: true,
    [DownloadFormat.PDF]: true,
    [DownloadFormat.CSV]: hasDownloadCsvDataPermission,
    [DownloadFormat.CSVCLIPBOARD]: hasDownloadCsvDataPermission,
  };

  const hasFeatureAccessMap = {
    [DownloadFormat.PNG]: true,
    [DownloadFormat.PDF]: true,
    [DownloadFormat.CSV]: hasCanExportCsvFeatureGate,
    [DownloadFormat.CSVCLIPBOARD]: hasCanExportCsvFeatureGate,
  };

  const defaultTooltipConfigMap = {
    [DownloadFormat.PNG]: {},
    [DownloadFormat.PDF]: {},
    [DownloadFormat.CSV]: exportCsvDefaultTooltipConfig
      ? {tooltip: {...exportCsvDefaultTooltipConfig, placement: Placement.Left}}
      : {},
    [DownloadFormat.CSVCLIPBOARD]: exportCsvDefaultTooltipConfig
      ? {tooltip: {...exportCsvDefaultTooltipConfig, placement: Placement.Left}}
      : {},
  };

  const exportFormatLabel = {
    [DownloadFormat.PNG]: ({t}) => t(`reports.actions.exports.export-png`),
    [DownloadFormat.PDF]: ({t}) => t(`reports.actions.exports.export-pdf`),
    [DownloadFormat.CSV]: ({t}) => t(`reports.actions.exports.export-csv`),
    [DownloadFormat.CSVCLIPBOARD]: ({t}) => t(`reports.actions.exports.export-csvclipboard`),
  };

  const exportFormatIcon = {
    [DownloadFormat.PNG]: `export-${DownloadFormat.PNG.toLowerCase()}`,
    [DownloadFormat.PDF]: `export-${DownloadFormat.PDF.toLowerCase()}`,
    [DownloadFormat.CSV]: `export-${DownloadFormat.CSV.toLowerCase()}`,
    [DownloadFormat.CSVCLIPBOARD]: `copy`,
  };

  return [
    {
      label: ({t}) => t(`reports.actions.exports.export`),
      icon: `export`,
      subSections: [
        {
          items: downloadFormats
            .filter((format) => hasPermissionMap[format])
            .map((format) => {
              if (hasFeatureAccessMap[format]) {
                return {
                  icon: exportFormatIcon[format],
                  label: exportFormatLabel[format],
                  value: `${DOWNLOAD_PREFIX}${format}`,
                  isDisabled: !hasPermissionMap[format],
                  ...(!hasPermissionMap[format]
                    ? {tooltip: {text: ({t}) => t(`reports.actions.exports.enable-cta`), placement: Placement.Left}}
                    : defaultTooltipConfigMap[format]),
                };
              }
              return {
                value: `${DOWNLOAD_PREFIX}${format}`,
                icon: `export-${format.toLowerCase()}`,
                label: exportFormatLabel[format],
                tooltip: {
                  text: ({t}) => t(`reports.actions.exports.upgrade-cta`, {format}),
                  placement: Placement.Left,
                },
                isDisabled: true,
                href: `/pricing/`,
                target: `_blank`,
                wrapLabel: true,
              };
            }),
        },
      ],
    },
  ];
}
